import clsx from 'clsx'
import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { createPortal } from 'react-dom'

import type { ItemDeDownload } from '@/hooks/use-bandeja-de-downloads'
import {
  estaPendente,
  inscrever,
  limparItens,
  obterItens,
  removerItem,
} from '@/hooks/use-bandeja-de-downloads/store'

import { DialogoDeCancelamento } from './components/DialogoDeCancelamento'
import { ItemDaBandeja } from './components/ItemDaBandeja'
import classes from './styles.module.scss'
import { obterTituloDaBandeja } from './utils'

const VARIAVEL_DE_ALTURA = '--altura-bandeja-de-downloads'

function usePublicarAltura(elemento: HTMLElement | null): void {
  useEffect(() => {
    const raiz = document.documentElement

    if (!elemento) {
      raiz.style.removeProperty(VARIAVEL_DE_ALTURA)
      return undefined
    }

    const observador = new ResizeObserver(([entrada]) => {
      raiz.style.setProperty(
        VARIAVEL_DE_ALTURA,
        `${Math.ceil(entrada.borderBoxSize?.[0]?.blockSize ?? elemento.offsetHeight)}px`
      )
    })

    observador.observe(elemento)

    return () => {
      observador.disconnect()
      raiz.style.removeProperty(VARIAVEL_DE_ALTURA)
    }
  }, [elemento])
}

export function BandejaDeDownloads(): JSX.Element | null {
  const itens = useSyncExternalStore(inscrever, obterItens)
  const [minimizada, setMinimizada] = useState(false)
  const [itemACancelar, setItemACancelar] = useState<ItemDeDownload | null>(
    null
  )
  const [confirmandoFechamento, setConfirmandoFechamento] = useState(false)
  const [bandeja, setBandeja] = useState<HTMLElement | null>(null)
  const quantidadeAnteriorRef = useRef(itens.length)

  usePublicarAltura(bandeja)

  const pendentes = itens.filter(estaPendente)
  const titulo = obterTituloDaBandeja(itens)

  useEffect(() => {
    if (itens.length > quantidadeAnteriorRef.current) setMinimizada(false)
    if (itens.length === 0) setMinimizada(false)

    quantidadeAnteriorRef.current = itens.length
  }, [itens.length])

  useEffect(() => {
    if (pendentes.length === 0) return undefined

    function alertarSobreDownloadsEmAndamento(evento: BeforeUnloadEvent): void {
      evento.preventDefault()
    }

    window.addEventListener('beforeunload', alertarSobreDownloadsEmAndamento)

    return () => {
      window.removeEventListener(
        'beforeunload',
        alertarSobreDownloadsEmAndamento
      )
    }
  }, [pendentes.length])

  function fecharBandeja(): void {
    if (pendentes.length > 0) {
      setConfirmandoFechamento(true)
      return
    }

    limparItens()
  }

  function confirmarFechamento(): void {
    for (const item of pendentes) {
      item.aoCancelar?.()
    }

    setConfirmandoFechamento(false)
    limparItens()
  }

  function confirmarCancelamentoDoItem(): void {
    if (!itemACancelar) return

    const atual = obterItens().find(item => item.id === itemACancelar.id)

    if (atual && estaPendente(atual)) {
      atual.aoCancelar?.()
      removerItem(atual.id)
    }

    setItemACancelar(null)
  }

  if (itens.length === 0) return null

  return createPortal(
    <>
      <section
        ref={setBandeja}
        className={clsx(classes.bandeja, minimizada && classes.minimizada)}
        aria-label="Bandeja de downloads"
      >
        <header className={classes.cabecalho}>
          <h2 className={classes.titulo} aria-live="polite">
            {titulo}
          </h2>

          <div className={classes.controles}>
            <button
              type="button"
              className={classes.botaoControle}
              onClick={() => setMinimizada(atual => !atual)}
              aria-label={minimizada ? 'Expandir bandeja' : 'Minimizar bandeja'}
              aria-expanded={!minimizada}
              aria-controls="lista-bandeja-de-downloads"
            >
              <i
                className={
                  minimizada
                    ? 'fa-regular fa-up-right-and-down-left-from-center'
                    : 'fa-regular fa-minus'
                }
                aria-hidden="true"
              />
            </button>

            <button
              type="button"
              className={classes.botaoControle}
              onClick={fecharBandeja}
              aria-label="Fechar bandeja"
            >
              <i className="fa-regular fa-xmark" aria-hidden="true" />
            </button>
          </div>
        </header>

        <AnimatePresence initial={false}>
          {!minimizada && (
            <motion.div
              key="lista"
              initial={{ height: 0 }}
              animate={{ height: 'auto' }}
              exit={{ height: 0 }}
              transition={{ duration: 0.2 }}
              style={{ overflow: 'hidden' }}
            >
              <ul id="lista-bandeja-de-downloads" className={classes.lista}>
                {itens.map(item => (
                  <ItemDaBandeja
                    key={item.id}
                    item={item}
                    aoSolicitarCancelamento={setItemACancelar}
                  />
                ))}
              </ul>
            </motion.div>
          )}
        </AnimatePresence>
      </section>

      <DialogoDeCancelamento
        deveSerExibido={Boolean(itemACancelar)}
        titulo="Cancelar download em andamento?"
        descricao="O download será cancelado e o progresso atual será perdido."
        aoConfirmar={confirmarCancelamentoDoItem}
        aoFechar={() => setItemACancelar(null)}
      />

      <DialogoDeCancelamento
        deveSerExibido={confirmandoFechamento}
        titulo="Cancelar downloads em andamento?"
        descricao={`O download de ${pendentes.length} arquivo(s) será cancelado e o progresso atual será perdido. Arquivos já concluídos continuarão salvos.`}
        aoConfirmar={confirmarFechamento}
        aoFechar={() => setConfirmandoFechamento(false)}
      />
    </>,
    document.body
  )
}
